import * as R from 'ramda';
import {
  put,
  call,
  take,
  select,
  takeLatest,
} from 'redux-saga/effects';
// components
import { closeLoader, openLoader } from '../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import {
  getSequencesByTypeRequest,
  getSequencesByTypeSuccess,
} from '../sequence/actions';
import {
  getAllAvailableRefTypesByScopeRequest,
  getAllAvailableRefTypesByScopeRequestSuccess,
} from '../reference/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { crudSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature configs
import * as A from './actions';
import { makeSelectConfigByName, makeSelectCurrentGroupName } from './selectors';
//////////////////////////////////////////////////

export function* getConfigsByNamesAndSetToWindow(names) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        names,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const mapped = G.mapConfigValuesByName(data);

      yield call(G.setAmousConfigToWindow, mapped);
    } else {
      yield call(G.handleFailResponse, res, 'getConfigsByNamesAndSetToWindow fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getConfigsByNamesAndSetToWindow exception');
  }
}

export function* getConfigByGroupSaga(group) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    let groupName = group;

    if (G.isNilOrEmpty(group)) {
      groupName = yield select(makeSelectCurrentGroupName());
    }

    const options = {
      params: {
        group: groupName,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(
      sendRequest,
      'get',
      endpointsMap.branchConfigsEndpoint,
      options,
    );

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getConfigByGroupSuccess({
        groupName,
        res: data,
      }));
    } else {
      yield call(G.handleFailResponse, res, 'getConfigByGroupSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getConfigByGroupSaga');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

function* handleUpdateConfigsSaga({ payload }) {
  try {
    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const currentGroupName = yield select(makeSelectCurrentGroupName());
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
      data: payload,
    };
    const res = yield call(
      sendRequest,
      'put',
      endpointsMap.branchConfigsEndpoint,
      options,
    );
    const { data, status } = res;
    if (G.isResponseSuccess(status, data)) {
      if (G.isNotPartiallySuccess(status)) {
        if (R.equals(currentGroupName, GC.UI_CONFIG_GROUP)) {
          yield call(G.showToastrMessage, 'info', 'messages:login:applied');
        } else {
          yield call(G.showToastrMessage, 'success', 'messages:success:save');
        }
      }
      yield put(A.updateConfigsSuccess(currentGroupName));
      yield call(getConfigByGroupSaga, currentGroupName);
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateConfigsSaga fail');
    }
    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleUpdateConfigsSaga exception');
  }
}

function* removeOptionalConfigRequestSaga({ payload }) {
  try {
    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const currentGroupName = yield select(makeSelectCurrentGroupName());
    const options = {
      params: {
        configName: payload,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    const res = yield call(
      sendRequest,
      'delete',
      endpointsMap.branchConfigsEndpoint,
      options,
    );
    const { data, status } = res;
    if (G.isResponseSuccess(status, data)) {
      if (G.isNotPartiallySuccess(status)) {
        yield call(G.showToastrMessage, 'success', 'messages:success:remove');
      }
      yield call(getConfigByGroupSaga, currentGroupName);
    } else {
      yield call(G.handleFailResponse, res, 'removeOptionalConfigRequestSaga fail');
    }
    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'removeOptionalConfigRequestSaga exception');
  }
}

function* restoreInheritedDropdownSaga({ payload }) {
  try {
    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const currentGroupName = yield select(makeSelectCurrentGroupName());
    const dropdownName = payload;
    const options = {
      params: {
        dropdownName,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    const res = yield call(
      sendRequest,
      'put',
      endpointsMap.dropdownOptionConfigRestoreInherited,
      options,
    );
    const { status } = res;
    if (G.isResponseSuccess(status)) {
      yield call(getConfigByGroupSaga, currentGroupName);
      yield call(G.showToastrMessage, 'success', 'messages:success:save');
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateConfigsSaga fail');
    }
    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleUpdateConfigsSaga exception');
  }
}

function* handleGetReferenceTypesGuidsNamesMap({ payload }) {
  try {
    const options = {
      data: {
        elements: payload,
      },
    };
    const res = yield call(
      sendRequest,
      'post',
      endpointsMap.referenceTypeGuidsNamesMap,
      options,
    );
    const { data, status } = res;
    if (G.isResponseSuccess(status)) {
      yield put(A.getReferenceTypesGuidsNamesMapSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetReferenceTypesGuidsNamesMap fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetReferenceTypesGuidsNamesMap exception');
  }
}

function* restoreInheritedByConfigTypeRequest({ payload }) {
  try {
    const customConfigsMap = {
      [GC.CUSTOM_DOCUMENT_CONFIG]: {
        getListAction: A.getDocumentsForConfigRequest,
        endpoint: endpointsMap.documentRestoreInherited,
      },
      [GC.CARRIER_DEACTIVATION_RULES]: {
        getListAction: A.getCarrierDeactivationRuleListRequest,
        endpoint: endpointsMap.carrierDeactivationRuleRestoreInherited,
      },
      [GC.CUSTOM_ACCESSORIALS_CONFIG]: {
        getListAction: A.getAccessorialForConfigRequest,
        endpoint: endpointsMap.restoreInheritedAccessorialsEndpoint,
      },
      [GC.CUSTOM_SERVICE_MAPPING_CONFIG_SECTION]: {
        getListAction: A.getServiceMappingListRequest,
        endpoint: endpointsMap.serviceMappingRestoreInherited,
      },
      [GC.CUSTOM_STATUS_MESSAGES_CONFIG]: {
        method: 'get',
        getListAction: A.getStatusMessagesForConfigRequest,
        endpoint: endpointsMap.restoreInheritedStatusMessagesEndpoint,
      },
      [GC.CUSTOM_TOLL_CHARGES_CONFIG]: {
        getListAction: A.getTollChargesConfigListRequest,
        endpoint: endpointsMap.tollChargeConfigRestoreInherited,
      },
      [GC.CUSTOM_ADD_NOTIFICATION_CONFIG]: {
        getListAction: A.getNotificationListTriggersRequest,
        endpoint: endpointsMap.notificationsRestoreInherited,
      },
      [GC.CUSTOM_ADVANCE_PAYMENT_CONFIG]: {
        getListAction: A.getAdvancePaymentListRequest,
        endpoint: endpointsMap.advancePaymentConfigRestoreInherited,
      },
      [GC.CARRIER_ONBOARDING_PACKAGE]: {
        getListAction: A.getCarrierOnboardingPackageListRequest,
        endpoint: endpointsMap.carrierOnboardingPackageRestoreInherited,
      },
      [GC.CUSTOM_CARRIER_RATE_CONFIG]: {
        getListAction: A.getCarrierRateIntegrationListRequest,
        endpoint: endpointsMap.carrierRateIntegrationConfigRestoreInherited,
      },
      [GC.CUSTOM_USER_MONITORING_CONFIG]: {
        getListAction: A.getUserMonitoringIntegrationListRequest,
        endpoint: endpointsMap.userMonitoringConfigRestoreInherited,
      },
      [GC.CUSTOM_LOADBOARD_CONFIG]: {
        endpoint: endpointsMap.loadBoardConfigRestoreInherited,
        getListAction: A.getLoadBoardIntegrationConfigListRequest,
      },
      [GC.CUSTOM_DISTANCE_CALCULATOR_CONFIG]: {
        getListAction: A.getDistanceCalculatorConfigListRequest,
        endpoint: endpointsMap.distanceCalculatorConfigRestoreInherited,
      },
      [GC.CUSTOM_GPS_INTEGRATION_CONFIG]: {
        getListAction: A.getGpsIntegrationConfigListRequest,
        endpoint: endpointsMap.eldIntegrationConfigRestoreInherited,
      },
      [GC.CUSTOM_INTEGRATION_CONFIGS_CONFIG]: {
        getListAction: A.getIntegrationConfigListRequest,
        endpoint: endpointsMap.customerIntegrationConfigRestoreInherited,
      },
      [GC.CUSTOM_DISPATCH_INTEGRATION_CONFIG]: {
        getListAction: A.getDispatchIntegrationConfigListRequest,
        endpoint: endpointsMap.dispatchIntegrationRestoreInherited,
      },
      [GC.CUSTOM_STATUS_CODE_MAPPING_CONFIG]: {
        getListAction: A.getStatusCodeMappingListRequest,
        endpoint: endpointsMap.statusCodeMappingRestoreInherited('inboundMapping'),
      },
      [GC.CUSTOM_STATUS_CODE_OUTBOUND_MAPPING_CONFIG]: {
        getListOptions: 'outboundMapping',
        getListAction: A.getStatusCodeMappingListRequest,
        endpoint: endpointsMap.statusCodeMappingRestoreInherited('outboundMapping'),
      },
      [GC.CUSTOM_STATUS_REASON_CODE_MAPPING_CONFIG]: {
        getListAction: A.getStatusReasonCodeMappingListRequest,
        endpoint: endpointsMap.statusReasonCodeMappingRestoreInherited('inboundMapping'),
      },
      [GC.CUSTOM_STATUS_REASON_CODE_OUTBOUND_MAPPING_CONFIG]: {
        getListOptions: 'outboundMapping',
        getListAction: A.getStatusReasonCodeMappingListRequest,
        endpoint: endpointsMap.statusReasonCodeMappingRestoreInherited('outboundMapping'),
      },
      [GC.CUSTOM_QUICK_BOOKS_ACCOUNT_MAPPING_CONFIG]: {
        getListAction: A.getQBAccountMappingListRequest,
        endpoint: endpointsMap.quickBooksAccountMappingRestoreInherited,
      },
      [GC.CUSTOM_QUICK_BOOKS_CUSTOM_FIELD_MAPPING_CONFIG]: {
        getListAction: A.getQBCustomFieldMappingListRequest,
        endpoint: endpointsMap.quickBooksCustomFieldMappingRestoreInherited,
      },
      [GC.CUSTOM_QUICK_BOOKS_TAX_CODE_MAPPING_CONFIG]: {
        getListAction: A.getQBTaxCodeMappingListRequest,
        endpoint: endpointsMap.quickBooksTaxCodeMappingRestoreInherited,
      },
      [GC.CUSTOM_QUICK_BOOKS_IIF_ASSESSORIAL_MAPPING_CONFIG]: {
        getListAction: A.getQBIIFAccessorialMappingListRequest,
        endpoint: endpointsMap.quickBooksIffAssessorialMappingRestoreInherited,
      },
      [GC.CUSTOM_FUEL_CARD_TRANSACTION_MAPPING_CONFIG]: {
        getListAction: A.getFuelCardTransactionMappingListRequest,
        endpoint: endpointsMap.fuelCardTransactionMappingRestoreInherited,
      },
      [GC.CUSTOM_SAGE_ACCOUNT_MAPPING_CONFIG]: {
        getListAction: A.getSageAccountMappingListRequest,
        endpoint: endpointsMap.sageAccountMappingRestoreInherited,
      },
      [GC.CUSTOM_CUSTOMER_ID_MAPPING_CONFIG]: {
        getListAction: A.getCustomerIdMappingListRequest,
        endpoint: endpointsMap.customerIdMappingRestoreInherited,
      },
      [GC.CUSTOM_DEPARTMENT_ID_MAPPING_CONFIG]: {
        getListAction: A.getDepartmentIdMappingListRequest,
        endpoint: endpointsMap.departmentIdMappingListRestoreInherited,
      },
      [GC.CUSTOM_MARGIN_VIOLATION_RULE_CONFIG]: {
        getListAction: A.getMarginViolationRuleListRequest,
        endpoint: endpointsMap.marginViolationRuleListRestoreInherited,
      },
      [GC.CUSTOM_SERVICE_TYPE_MAPPING_CONFIG]: {
        getListAction: A.getServiceTypeMappingListRequest,
        endpoint: endpointsMap.serviceTypeMappingRestoreInherited,
      },
      [GC.CUSTOM_GL_CODE_MAPPING_CONFIG]: {
        getListAction: A.getGLCodeMappingListRequest,
        endpoint: endpointsMap.glCodeRestoreInherited,
      },
      [GC.CUSTOM_CARRIER_INTEGRATION_OVERRIDE_CONFIG]: {
        getListAction: A.getCarrierIntegrationOverrideListRequest,
        endpoint: endpointsMap.restoreInheritedCarrierIntegrationCustomerConfig,
      },
      [GC.CUSTOM_INTEGRATION_DOCUMENT_TYPE_MAPPING_CONFIG]: {
        getListAction: A.getDocumentTypeMappingListRequest,
        endpoint: endpointsMap.restoreInheritedDocumentTypeMapping,
      },
      [GC.CUSTOM_INTEGRATION_OUTBOUND_DOCUMENT_TYPE_MAPPING_CONFIG]: {
        getListAction: A.getOutboundDocumentTypeMappingListRequest,
        endpoint: endpointsMap.restoreInheritedOutboundDocumentTypeMapping,
      },
      [GC.CUSTOM_INVOICE_STATUS_RULE_CONFIG]: {
        getListAction: A.getInvoiceStatusRuleListRequest,
        endpoint: endpointsMap.restoreInheritedDefaultInvoiceStatusRule,
      },
      [GC.CUSTOM_TRANSPORTATION_MODE_MAPPING_CONFIG]: {
        getListAction: A.getTransportationModeConfigMappingListRequest,
        endpoint: endpointsMap.transportationModeIconMappingRestoreInherited,
      },
      [GC.CUSTOM_EXTERNAL_TRANSPORTATION_MODE_MAPPING_CONFIG]: {
        getListOptions: GC.INTEGRATION_CONFIG_GROUP,
        getListAction: A.getTransportationModeConfigMappingListRequest,
        endpoint: endpointsMap.transportationModeMappingRestoreInherited,
      },
      [GC.CUSTOM_LOAD_CUSTOM_STATUS_CONFIG]: {
        getListAction: A.getLoadCustomStatusListRequest,
        endpoint: endpointsMap.loadCustomStatusRestoreInherited,
      },
      [GC.CUSTOM_EQUIPMENT_MAPPING_CONFIG]: {
        getListAction: A.getEquipmentMappingConfigListRequest,
        endpoint: endpointsMap.equipmentMappingRestoreInherited,
      },
      [GC.CUSTOM_CARRIER_SCORECARD_CONFIG]: {
        getListAction: A.getCarrierScorecardConfigListRequest,
        endpoint: endpointsMap.carrierScorecardConfigRestoreInherited,
      },
      [GC.CUSTOM_TASK_TYPE_CONFIG]: {
        getListAction: A.getTaskTypeListRequest,
        endpoint: endpointsMap.taskTypeConfigRestoreInherited,
      },
      [GC.CUSTOM_TASK_STATUS_CONFIG]: {
        getListAction: A.getTaskStatusListRequest,
        endpoint: endpointsMap.taskStatusConfigRestoreInherited,
      },
      [GC.CUSTOM_DOCUMENT_SENDING_CONFIG]: {
        getListAction: A.getDocumentSendingConfigListRequest,
        endpoint: endpointsMap.documentSendingConfigRestoreInherited,
      },
      [GC.CUSTOM_TRANSPORTATION_MODE_GROUPING_CONFIG]: {
        getListAction: A.getTransportationModeGroupingListRequest,
        endpoint: endpointsMap.transportationModeGroupingRestoreInherited,
      },
      [GC.CUSTOM_DEFAULT_CARRIER_ACCESSORIAL_CONFIG]: {
        getListAction: A.getDefaultCarrierAccessorialListRequest,
        endpoint: endpointsMap.defaultCarrierAccessorialRestoreInherited,
      },
      [GC.CUSTOM_EXPENSE_TYPES_CONFIG]: {
        getListAction: A.getExpenseTypeListRequest,
        endpoint: endpointsMap.driverExpenseTypeRestoreInherited,
      },
      [GC.CUSTOM_TRUCK_TYPE_MAPPING_CONFIG]: {
        getListAction: A.getTruckTypeMappingListRequest,
        endpoint: endpointsMap.truckTypeMappingRestoreInherited,
      },
      [GC.CUSTOM_TRAILER_TYPE_MAPPING_CONFIG]: {
        getListAction: A.getTrailerTypeMappingListRequest,
        endpoint: endpointsMap.trailerTypeMappingRestoreInherited,
      },
      [GC.CUSTOM_EQUIPMENT_SERVICE_AUTO_CREATE_CONFIG]: {
        getListAction: A.getEquipmentServiceAutoCreateConfigListRequest,
        getListOptions: G.ifElse(R.includes('truck', G.getWindowLocationPathname()), 'truck', 'trailer'),
        endpoint: endpointsMap.fleetEquipmentServiceAutoCreateConfigRestoreInherited(
          G.ifElse(R.includes('truck', G.getWindowLocationPathname()), 'truck', 'trailer'),
        ),
      },
      [GC.CUSTOM_INBOUND_REFERENCE_TYPE_MAPPING_CONFIG]: {
        getListOptions: 'inboundMapping',
        getListAction: A.getReferenceTypeMappingListRequest,
        endpoint: endpointsMap.referenceTypeMappingRestoreInherited('inboundMapping'),
      },
      [GC.CUSTOM_OUTBOUND_REFERENCE_TYPE_MAPPING_CONFIG]: {
        getListOptions: 'outboundMapping',
        getListAction: A.getReferenceTypeMappingListRequest,
        endpoint: endpointsMap.referenceTypeMappingRestoreInherited('outboundMapping'),
      },
      [GC.CUSTOM_ACCESSORIAL_OUTBOUND_MAPPING_CONFIG]: {
        getListOptions: 'outbound',
        getListAction: A.getAccessorialMappingListRequest,
        endpoint: endpointsMap.accessorialOutboundMappingRestoreInherited,
      },
      [GC.CUSTOM_CONTAINER_TRACKING_CONFIG]: {
        getListAction: A.getContainerTrackingConfigListRequest,
        endpoint: endpointsMap.containerTrackingConfigRestoreInherited,
      },
      [GC.CUSTOM_CRM_INTEGRATION_CONFIG]: {
        getListAction: A.getCRMIntegrationConfigListRequest,
        endpoint: endpointsMap.crmIntegrationConfigRestoreInherited,
      },
      [GC.CUSTOM_INBOUND_SERVICE_MAPPING_CONFIG]: {
        getListOptions: 'inboundMapping',
        getListAction: A.getServiceMappingListByTypeRequest,
        endpoint: endpointsMap.serviceMappingRestoreInheritedByType('inboundMapping'),
      },
      [GC.CUSTOM_OUTBOUND_SERVICE_MAPPING_CONFIG]: {
        getListOptions: 'outboundMapping',
        getListAction: A.getServiceMappingListByTypeRequest,
        endpoint: endpointsMap.serviceMappingRestoreInheritedByType('outboundMapping'),
      },
      [GC.CUSTOM_INBOUND_ORDER_TYPE_MAPPING_CONFIG]: {
        getListOptions: 'inboundMapping',
        getListAction: A.getOrderTypeMappingListByTypeRequest,
        endpoint: endpointsMap.orderTypeMappingRestoreInheritedByType('inboundMapping'),
      },
      [GC.CUSTOM_FREIGHT_SAFETY_CONFIG]: {
        getListAction: A.getFreightSafetyConfigListRequest,
        endpoint: endpointsMap.freightSafetyConfigRestoreInherited,
      },
      [GC.CUSTOM_PROPEL_HR_ASSESSORIAL_MAPPING_CONFIG]: {
        getListAction: A.getPropelHRAssessorialMappingListRequest,
        endpoint: endpointsMap.propelHRAssessorialMappingRestoreInherited,
      },
      [GC.CUSTOM_PAYLOCITY_CODE_MAPPING_CONFIG]: {
        getListAction: A.getPaylocityCodeMappingListRequest,
        endpoint: endpointsMap.paylocityCodeMappingRestoreInherited,
      },
    };

    if (G.notContain(payload, R.keys(customConfigsMap))) {
      return yield put(A.restoreInheritedDropdownRequest(payload));
    }

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const endpoint = R.path([payload, 'endpoint'], customConfigsMap);
    const method = R.pathOr('put', [payload, 'method'], customConfigsMap);
    const successAction = R.path([payload, 'getListAction'], customConfigsMap);

    const res = yield call(sendRequest, method, endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(successAction(R.path([payload, 'getListOptions'], customConfigsMap)));

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'restoreInheritedByConfigTypeRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, 'restoreInheritedByConfigTypeRequest exception');
  }
}

function* handleGetSequencesGuidNameMap({ payload }) {
  try {
    const options = {
      data: {
        elements: payload,
      },
    };
    const res = yield call(
      sendRequest,
      'post',
      endpointsMap.sequencesGuidNameMap,
      options,
    );
    const { data, status } = res;
    if (G.isResponseSuccess(status)) {
      yield put(A.getSequencesGuidNameMapSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetSequencesGuidNameMap fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetSequencesGuidNameMap exception');
  }
}

export function* getReferenceTypesByScopeForConfigSaga({ payload }) {
  try {
    yield put(getAllAvailableRefTypesByScopeRequest(payload));
    const res = yield take(getAllAvailableRefTypesByScopeRequestSuccess);
    yield put(A.getReferenceTypesByScopeForConfigSuccess({
      scope: payload,
      refTypes: res.payload,
    }));
  } catch (error) {
    yield call(G.handleException, error, 'getReferenceTypesByScopeForConfigSaga exception');
  }
}

export function* getSequencesByTypeForConfigSaga({ payload }) {
  try {
    yield put(getSequencesByTypeRequest(payload));
    const res = yield take(getSequencesByTypeSuccess);
    yield put(A.getSequencesByTypeForConfigSuccess({
      type: payload,
      sequences: res.payload,
    }));
  } catch (error) {
    yield call(G.handleException, error, 'getSequencesByTypeForConfigSaga exception');
  }
}

function* handleGetAvailableDocumentTypesRequestSaga() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(
      sendRequest,
      'get',
      endpointsMap.availableDocumentTypes,
      options,
    );

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.receivedAvailableDocumentTypesSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetAvailableDocumentTypesRequestSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetAvailableDocumentTypesRequestSaga exception');
  }
}

export function* getCustomerBranchListRequest() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: {
        [GC.BRANCH_TYPE]: GC.BRANCH_TYPE_ENUM_CUSTOMER,
      },
    };
    yield call(crudSaga, {
      options,
      method: 'get',
      successAction: A.getCustomerBranchListSuccess,
      parentSagaName: 'getCustomerBranchListRequest',
      endpoint: endpointsMap.getBranchListByType(branchGuid),
    });
  } catch (error) {
    yield call(G.handleException, error, 'getCustomerBranchListRequest exception');
  }
}

export function* getSharedAccessorialListSaga() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    yield call(crudSaga, {
      options,
      method: 'get',
      endpoint: endpointsMap.sharedAccessorialList,
      parentSagaName: 'getSharedAccessorialListSaga',
      successAction: A.getSharedAccessorialListSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getSharedAccessorialListSaga exception');
  }
}

const filterStatusMessage = (data) => (
  R.compose(
    G.mapDisplayedValueOriginalConfigGuidObjectPropsToLabelValueObject,
    R.filter(
      (item) => {
        const { statusType } = item;

        return G.ifElse(
          R.or(
            R.equals(statusType, GC.STATUS_CODE_STATUS_TYPE_DROP_COMPLETED),
            R.equals(statusType, GC.STATUS_CODE_STATUS_TYPE_PICKUP_COMPLETED),
          ),
          null,
          item,
        );
      },
    ),
  )(data)
);

const getStatusCodeOptionsByStatusType = (statusCodes, statusType) => R.compose(
  G.mapDisplayedValueOriginalConfigGuidObjectPropsToLabelValueObject,
  R.filter(R.propEq(statusType, GC.FIELD_STATUS_TYPE)),
)(statusCodes);

export function* getStatusMessagesForConfigSaga() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { [GC.FIELD_BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.statusMessagesListEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const statusCodeList = filterStatusMessage(data);

      const allStatusCodeList = G.mapDisplayedValueOriginalConfigGuidObjectPropsToLabelValueObject(data);

      const allStatusCodeListWithStoredValues =
        G.mapDisplayedValueOriginalConfigGuidObjectPropsToLabelValueObject(data, true);

      const trackingStatusCodeList = R.compose(
        G.mapDisplayedValueOriginalConfigGuidObjectPropsToLabelValueObject,
        R.filter(R.prop(GC.FIELD_STATUS_MESSAGE_TRACKING)),
      )(data);

      const gpsStatusCodeList = R.compose(
        G.mapDisplayedValueOriginalConfigGuidObjectPropsToLabelValueObject,
        R.filter(({ tracking, statusType }) => R.and(G.isTrue(tracking), G.isNilOrEmpty(statusType))),
      )(data);

      const pickupCompletedStatusCodes = getStatusCodeOptionsByStatusType(
        data,
        GC.STATUS_CODE_STATUS_TYPE_PICKUP_COMPLETED,
      );

      const dropCompletedStatusCodes = getStatusCodeOptionsByStatusType(
        data,
        GC.STATUS_CODE_STATUS_TYPE_DROP_COMPLETED,
      );

      const stopCheckedInStatusCodes = getStatusCodeOptionsByStatusType(
        data,
        GC.STATUS_CODE_STATUS_TYPE_STOP_CHECKED_IN,
      );

      const pickupCheckedInStatusCodes = getStatusCodeOptionsByStatusType(
        data,
        GC.STATUS_CODE_STATUS_TYPE_PICKUP_CHECKED_IN,
      );

      const dropCheckedInStatusCodes = getStatusCodeOptionsByStatusType(
        data,
        GC.STATUS_CODE_STATUS_TYPE_DROP_CHECKED_IN,
      );

      const checkedInStatusCodesForDrop = R.concat(dropCheckedInStatusCodes, stopCheckedInStatusCodes);
      const checkedInStatusCodesForPickup = R.concat(pickupCheckedInStatusCodes, stopCheckedInStatusCodes);

      yield put(A.getStatusCodeListSuccess({
        statusCodeList,
        allStatusCodeList,
        gpsStatusCodeList,
        trackingStatusCodeList,
        stopCheckedInStatusCodes,
        dropCompletedStatusCodes,
        dropCheckedInStatusCodes,
        pickupCompletedStatusCodes,
        pickupCheckedInStatusCodes,
        checkedInStatusCodesForDrop,
        checkedInStatusCodesForPickup,
        allStatusCodeListWithStoredValues,
      }));
    } else {
      yield call(G.handleFailResponse, res, 'getStatusMessagesForConfigSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getStatusMessagesForConfigSaga exception');
  }
}

// terms and conditions
const termsConfigGroupToDocumentEndpointMap = {
  [GC.GENERAL_CONFIG_GROUP]: endpointsMap.userTermsAndConditionsDocument,
  [GC.CLO_CONFIG_GROUP]: endpointsMap.customerTermsAndConditionsDocument,
  [GC.CARRIER_CONFIG_GROUP]: endpointsMap.carrierTermsAndConditionsDocument,
  [GC.DRIVER_CONFIG_GROUP]: endpointsMap.driverAppAcceptRateTermsAndConditionsDocument,
  [GC.INVOICE_CONFIG_GROUP]: endpointsMap.driverAppAcceptPayrollTermsAndConditionsDocument,
};

export function* getTermsAndConditionsDocumentRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const configGroup = R.pathOr(payload, ['configGroup'], payload);
    const branchGuidToUse = R.pathOr(branchGuid, ['branchGuid'], payload);

    const options = {
      params: { [GC.BRANCH_GUID]: branchGuidToUse },
    };

    const mapper = (data) => {
      if (G.isNilOrEmpty(data)) return configGroup;

      return R.assoc('configGroup', configGroup, data);
    };

    const endpoint = R.prop(configGroup, termsConfigGroupToDocumentEndpointMap);

    yield call(crudSaga, {
      mapper,
      options,
      endpoint,
      method: 'get',
      successAction: A.getTermsAndConditionsDocumentSuccess,
      parentSagaName: 'getTermsAndConditionsDocumentRequest',
    });
  } catch (error) {
    yield call(G.handleException, error, 'getTermsAndConditionsDocumentRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* createTermsAndConditionsDocumentRequest({ payload }) {
  try {
    yield put(openLoader());

    const { configGroup } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = { data: G.makeDataForDocument(R.assoc(GC.BRANCH_GUID, branchGuid, payload)) };

    const mapper = R.assoc('configGroup', configGroup);

    const endpoint = R.prop(configGroup, termsConfigGroupToDocumentEndpointMap);

    yield call(crudSaga, {
      mapper,
      options,
      endpoint,
      method: 'post',
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      successAction: A.getTermsAndConditionsDocumentSuccess,
      parentSagaName: 'createTermsAndConditionsDocumentRequest',
    });
  } catch (error) {
    yield call(G.handleException, error, 'createTermsAndConditionsDocumentRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* removeTermsAndConditionsDocumentRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guid, configGroup } = payload;

    const options = { params: { guid } };

    const endpoint = R.prop(configGroup, termsConfigGroupToDocumentEndpointMap);

    const res = yield call(sendRequest, 'delete', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getTermsAndConditionsDocumentSuccess(configGroup));

      yield call(G.showToastrMessage, 'success', 'messages:success:204');
    } else {
      yield call(G.handleFailResponse, res, 'removeTermsAndConditionsDocumentRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'removeTermsAndConditionsDocumentRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* updateTermsAndConditionsDocumentRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const { configGroup } = payload;

    const options = { data: G.makeDataForDocument(R.assoc(GC.BRANCH_GUID, branchGuid, payload)) };

    const mapper = R.assoc('configGroup', configGroup);

    const termsUpdateConfigGroupToDocumentEndpointMap = {
      [GC.GENERAL_CONFIG_GROUP]: endpointsMap.updateUserTermsAndConditionsDocument,
      [GC.CLO_CONFIG_GROUP]: endpointsMap.updateCustomerTermsAndConditionsDocument,
      [GC.CARRIER_CONFIG_GROUP]: endpointsMap.updateCarrierTermsAndConditionsDocument,
      [GC.DRIVER_CONFIG_GROUP]: endpointsMap.updateDriverAppAcceptRateTermsAndConditionsDocument,
      [GC.INVOICE_CONFIG_GROUP]: endpointsMap.updateDriverAppAcceptPayrollTermsAndConditionsDocument,
    };

    const endpoint = R.prop(configGroup, termsUpdateConfigGroupToDocumentEndpointMap);

    yield call(crudSaga, {
      mapper,
      options,
      endpoint,
      method: 'post',
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      successAction: A.getTermsAndConditionsDocumentSuccess,
      parentSagaName: 'updateTermsAndConditionsDocumentRequest',
    });
  } catch (error) {
    yield call(G.handleException, error, 'updateTermsAndConditionsDocumentRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

// transportation mode mapping
export function* getTransportationModeConfigMappingListRequest({ payload }) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };
    const mapper = (data) => ({ data: R.or(data, []), configGroup: payload });
    const endpoint = G.ifElse(
      R.equals(payload, GC.INTEGRATION_CONFIG_GROUP),
      endpointsMap.transportationModeMappingList,
      endpointsMap.transportationModeIconMappingList,
    );

    yield call(crudSaga, {
      mapper,
      options,
      endpoint,
      method: 'get',
      parentSagaName: 'getTransportationModeConfigMappingListRequest',
      successAction: A.getTransportationModeConfigMappingListSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getTransportationModeConfigMappingListRequest exception');
  }
}

function* createOrUpdateTransportationModeConfigMappingRequest({ payload }) {
  try {
    const { version, configGroup } = payload;

    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const method = G.ifElse(
      G.isNotNilAndNotEmpty(version),
      'put',
      'post',
    );
    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, branchGuid, payload),
    };
    const mapper = R.assoc('configGroup', configGroup);
    const endpoint = G.ifElse(
      R.equals(configGroup, GC.INTEGRATION_CONFIG_GROUP),
      endpointsMap.transportationModeMapping,
      endpointsMap.transportationModeIconMapping,
    );

    yield call(crudSaga, {
      mapper,
      method,
      options,
      endpoint,
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      successAction: A.createOrUpdateTransportationModeConfigMappingSuccess,
      parentSagaName: 'createOrUpdateTransportationModeConfigMappingRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'createOrUpdateTransportationModeConfigMappingRequest exception');
  }
}

function* removeTransportationModeConfigMappingRequest({ payload }) {
  try {
    const { guid, configGroup } = payload;

    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const endpoint = G.ifElse(
      R.equals(configGroup, GC.INTEGRATION_CONFIG_GROUP),
      endpointsMap.transportationModeMapping,
      endpointsMap.transportationModeIconMapping,
    );
    const options = {
      data: {
        guid,
        [GC.BRANCH_GUID]: branchGuid,
      },
    };
    const mapper = () => payload;

    yield call(crudSaga, {
      mapper,
      options,
      payload,
      endpoint,
      method: 'delete',
      showSuccessMessage: true,
      successMessage: 'messages:success:204',
      successAction: A.removeTransportationModeConfigMappingSuccess,
      parentSagaName: 'removeTransportationModeConfigMappingRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'removeTransportationModeConfigMappingRequest exception');
  }
}

// dropdown option config
function* createOrUpdateDropdownOptionConfigRequest({ payload }) {
  try {
    yield put(openLoader());

    const { dropdownName } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const dropdown = yield select(makeSelectConfigByName(dropdownName));

    const options = {
      data: R.assoc(GC.BRANCH_GUID, branchGuid, payload),
    };

    const method = G.ifElse(G.getGuidFromObject(payload), 'put', 'post');

    yield call(crudSaga, {
      method,
      options,
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      endpoint: endpointsMap.dropdownOptionConfig,
      parentSagaName: 'createOrUpdateDropdownOptionConfigRequest',
      callbackSaga: G.isNilOrEmpty(dropdown) ? getConfigByGroupSaga : null,
      successAction: G.isNilOrEmpty(dropdown) ? null : A.createOrUpdateDropdownOptionConfigSuccess,
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'createOrUpdateDropdownOptionConfigRequest exception');
  }
}

function* removeDropdownOptionConfigRequest({ payload }) {
  try {
    const { guid } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: { guid, [GC.BRANCH_GUID]: branchGuid },
    };

    yield call(crudSaga, {
      options,
      payload,
      method: 'delete',
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      endpoint: endpointsMap.dropdownOptionConfig,
      successAction: A.removeDropdownOptionConfigSuccess,
      parentSagaName: 'removeDropdownOptionConfigRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'removeDropdownOptionConfigRequest exception');
  }
}

// equipment service auto create config
function* getEquipmentServiceAutoCreateConfigListRequest({ payload }) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const mapper = (data) => ({ data: R.or(data, []), configGroup: payload });

    const fleetType = G.ifElse(R.equals(payload, GC.TRUCK_CONFIG_GROUP), 'truck', 'trailer');
    const endpoint = endpointsMap.fleetEquipmentServiceAutoCreateConfigList(fleetType);

    yield call(crudSaga, {
      mapper,
      options,
      endpoint,
      method: 'get',
      parentSagaName: 'getEquipmentServiceAutoCreateConfigListRequest',
      successAction: A.getEquipmentServiceAutoCreateConfigListSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getEquipmentServiceAutoCreateConfigListRequest exception');
  }
}

function* createOrUpdateEquipmentServiceAutoCreateConfigRequest({ payload }) {
  try {
    const { version, configGroup } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const method = G.ifElse(
      G.isNotNilAndNotEmpty(version),
      'put',
      'post',
    );

    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, branchGuid, payload),
    };

    const mapper = R.assoc('configGroup', configGroup);

    const fleetType = G.ifElse(R.equals(configGroup, GC.TRUCK_CONFIG_GROUP), 'truck', 'trailer');
    const endpoint = endpointsMap.fleetEquipmentServiceAutoCreateConfig(fleetType);

    yield call(crudSaga, {
      mapper,
      method,
      options,
      endpoint,
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      successAction: A.createOrUpdateEquipmentServiceAutoCreateConfigSuccess,
      parentSagaName: 'createOrUpdateEquipmentServiceAutoCreateConfigRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'createOrUpdateEquipmentServiceAutoCreateConfigRequest exception');
  }
}

function* removeEquipmentServiceAutoCreateConfigRequest({ payload }) {
  try {
    const { guid, configGroup } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const fleetType = G.ifElse(R.equals(configGroup, GC.TRUCK_CONFIG_GROUP), 'truck', 'trailer');
    const endpoint = endpointsMap.fleetEquipmentServiceAutoCreateConfig(fleetType);

    const options = {
      data: {
        guid,
        [GC.BRANCH_GUID]: branchGuid,
      },
    };

    const mapper = () => payload;

    yield call(crudSaga, {
      mapper,
      options,
      payload,
      endpoint,
      method: 'delete',
      showSuccessMessage: true,
      successMessage: 'messages:success:204',
      successAction: A.removeEquipmentServiceAutoCreateConfigSuccess,
      parentSagaName: 'removeEquipmentServiceAutoCreateConfigRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'removeEquipmentServiceAutoCreateConfigRequest exception');
  }
}

function* getConfigsForAuthWrapperRequest() {
  try {
    yield put(A.getQBDesktopConnectionListRequest());
  } catch (error) {
    yield call(G.handleException, error, 'getConfigsForAuthWrapperRequest exception');
  }
}

export function* getGroupConfigsByNamesRequest({ payload }) {
  try {
    const { names, groupName } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        names,
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getGroupConfigsByNamesSuccess({
        data,
        groupName,
      }));

      const mappedConfigs = G.mapConfigValuesByName(data);

      yield call(G.setAmousConfigToWindow, mappedConfigs);
    } else {
      yield call(G.handleFailResponse, res, 'getGroupConfigsByNamesRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getGroupConfigsByNamesRequest exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

function* configWatcherSaga() {
  yield takeLatest(A.updateConfigsRequest, handleUpdateConfigsSaga);
  yield takeLatest(A.removeOptionalConfigRequest, removeOptionalConfigRequestSaga);
  yield takeLatest(A.getGroupConfigsByNamesRequest, getGroupConfigsByNamesRequest);
  yield takeLatest(A.getSequencesGuidNameMapRequest, handleGetSequencesGuidNameMap);
  yield takeLatest(A.restoreInheritedDropdownRequest, restoreInheritedDropdownSaga);
  yield takeLatest(A.getConfigsForAuthWrapperRequest, getConfigsForAuthWrapperRequest);
  yield takeLatest(A.getAvailableDocumentTypes, handleGetAvailableDocumentTypesRequestSaga);
  yield takeLatest(A.restoreInheritedByConfigTypeRequest, restoreInheritedByConfigTypeRequest);
  yield takeLatest(A.getReferenceTypesGuidsNamesMapRequest, handleGetReferenceTypesGuidsNamesMap);
  // terms and conditions
  yield takeLatest(A.getTermsAndConditionsDocumentRequest, getTermsAndConditionsDocumentRequest);
  yield takeLatest(A.updateTermsAndConditionsDocumentRequest, updateTermsAndConditionsDocumentRequest);
  yield takeLatest(A.removeTermsAndConditionsDocumentRequest, removeTermsAndConditionsDocumentRequest);
  yield takeLatest(A.createTermsAndConditionsDocumentRequest, createTermsAndConditionsDocumentRequest);
  // transportation mode config mapping
  yield takeLatest(A.removeTransportationModeConfigMappingRequest, removeTransportationModeConfigMappingRequest);
  yield takeLatest(A.getTransportationModeConfigMappingListRequest, getTransportationModeConfigMappingListRequest);
  yield takeLatest(
    A.createOrUpdateTransportationModeConfigMappingRequest,
    createOrUpdateTransportationModeConfigMappingRequest,
  );
  // dropdown option config
  yield takeLatest(A.removeDropdownOptionConfigRequest, removeDropdownOptionConfigRequest);
  yield takeLatest(A.createOrUpdateDropdownOptionConfigRequest, createOrUpdateDropdownOptionConfigRequest);
  // equipment service auto create config
  yield takeLatest(A.removeEquipmentServiceAutoCreateConfigRequest, removeEquipmentServiceAutoCreateConfigRequest);
  yield takeLatest(A.getEquipmentServiceAutoCreateConfigListRequest, getEquipmentServiceAutoCreateConfigListRequest);
  yield takeLatest(
    A.createOrUpdateEquipmentServiceAutoCreateConfigRequest,
    createOrUpdateEquipmentServiceAutoCreateConfigRequest,
  );
}

export default configWatcherSaga;
